import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Can, useCan } from "@/auth/permissions";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Pencil, Tag } from "lucide-react";
import {
  costEngineBreakeven,
  costEngineCalculate,
  costEngineOccupancyTable,
  b2bRetailTag,
  costEnginePrice,
  costEngineVehicleOptions,
  listChannelConfig,
  pricingApi,
  INTERCITY_DIRECTION_LABEL,
} from "@/services/pricing";
import { getProduct } from "@/services/products";
import { OvernightMissingBar } from "@/pages/products/OvernightHotelSelect";   // HOTELGATE-1
import { OptionSwitch } from "@/pages/products/OptionSwitch";   // OPT-B2b
import { optionQueryKey, productOptionsApi } from "@/services/productOptions";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";
import { PricingSubnav } from "./PricingSubnav";

const usd = (c?: number | null) => (c == null ? "—" : `$${Math.round(c / 100).toLocaleString()}`);
const STATUS_CN: Record<string, string> = { draft: "草稿", published: "在售", archived: "已归档" };

/** 售价管理中心 · 产品售价详情（/admin/pricing/products/:id · canvas page-61）。 */
export function PricingProductDetailPage() {
  const { id } = useParams();
  const prodQ = useQuery({ queryKey: ["products", "detail", id], enabled: !!id, queryFn: () => getProduct(id!) });
  const maxPax = prodQ.data?.max_pax_per_departure ?? 13;
  const en = !!id && !!prodQ.data;

  // OPT-B2b · 选项 ≥ 2 个才显示切换；null = 默认选项（不带 option_id，请求与以前逐字节相同）
  const optsQ = useQuery({ queryKey: optionQueryKey(id ?? ""), enabled: !!id, queryFn: () => productOptionsApi.list(id!) });
  const [optPick, setOptPick] = useState<string | null>(null);
  const optionList = optsQ.data ?? [];
  const optId = optionList.find((o) => o.id === optPick && !o.is_default)?.id ?? null;
  const canCost = useCan("pricing.cost.view");   // RBACFIX-1 · 成本算价 / 保本都需要成本查看权限：没有就不请求、不显示保本卡
  const calcQ = useQuery({ queryKey: ["pricing", "calc", id, optId], enabled: en && canCost, queryFn: () => costEngineCalculate({ product_id: id!, pax: maxPax, occupancy: 2, ...(optId ? { option_id: optId } : {}) }) });
  const priceQ = useQuery({ queryKey: ["pricing", "price", id, optId], enabled: en, queryFn: () => costEnginePrice({ product_id: id!, pax: maxPax, occupancy: 2, ...(optId ? { option_id: optId } : {}) }) });
  const occBQ = useQuery({ queryKey: ["pricing", "occ", id, "b2b", optId], enabled: en, queryFn: () => costEngineOccupancyTable(id!, { channel: "b2b", optionId: optId }) });
  const occDQ = useQuery({ queryKey: ["pricing", "occ", id, "direct", optId], enabled: en, queryFn: () => costEngineOccupancyTable(id!, { channel: "direct", optionId: optId }) });
  // MFEE-1 · api 渠道（GYG/Bokun/OCTO/Viator 共用）——列表价已并入必付费，见下方「含必付」小字
  const occAQ = useQuery({ queryKey: ["pricing", "occ", id, "api", optId], enabled: en, queryFn: () => costEngineOccupancyTable(id!, { channel: "api", optionId: optId }) });
  const holQ = useQuery({ queryKey: ["pricing", "holidays"], queryFn: () => pricingApi.listHolidays() });
  const chCfgQ = useQuery({ queryKey: ["pricing", "channel-config"], queryFn: listChannelConfig });
  const b2bCfg = chCfgQ.data?.find((c) => c.channel === "b2b");   // CHOVR-1 · 佣金比例读系统设置，不再写死 30%

  // BKEVEN-1 · 保本按车型分 Tab——vehicle-options 已按座位数降序·默认选第一个（=现状表）
  const vehOptQ = useQuery({ queryKey: ["pricing", "vehicle-options", id], enabled: en, queryFn: () => costEngineVehicleOptions(id!) });
  const vehicleOptions = vehOptQ.data ?? [];
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  useEffect(() => { setActiveTabIdx(0); }, [id]); // 切换产品重置回第一个 Tab
  // 第一个 Tab（默认）不强制车型——与改前请求逐字节一致（A1 零回归）；其余 Tab 才传 vehicle_model
  const forcedModel = activeTabIdx > 0 ? vehicleOptions[activeTabIdx]?.model_label_cn : undefined;
  const beQ = useQuery({
    queryKey: ["pricing", "be", id, forcedModel ?? null, optId],
    enabled: en && canCost,
    queryFn: () => costEngineBreakeven(id!, { occupancy: 2, vehicleModel: forcedModel, optionId: optId }),
  });

  if (prodQ.isLoading) return <LoadingState />;
  if (prodQ.isError || !prodQ.data)
    return (
      <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
        <span>{prodQ.isError ? "产品加载失败" : "产品不存在"}</span>
        {prodQ.isError && <button onClick={() => prodQ.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>}
        <Link to="/admin/pricing" className="text-blue-600 hover:underline">返回售价总览</Link>
      </div>
    );
  const p = prodQ.data;
  const c = calcQ.data;
  const cs = priceQ.data;
  const b2b = cs?.channels.find((x) => x.channel === "b2b");
  const direct = cs?.channels.find((x) => x.channel === "direct");

  // 房价/晚 = 房价/人@2 × 2 ÷ 晚数；车队日成本 / 天 / 人 来自 calc
  const hotelBase2 = c?.hotel_per_person_cents ?? 0;          // 2 人一间 房价/人
  const roomNightCents = c && c.total_nights ? Math.round((hotelBase2 * 2) / c.total_nights) : 0;
  const carPerPerson = (n: number) => (c ? Math.round((c.fleet_daily_cost_cents * (c.bus_days ?? c.total_days)) / n) : 0);

  const subtitle = [
    p ? `${p.departure_city}出发` : "",
    p ? `${p.end_city}结束` : "",
    `最大 ${maxPax} 人`,
    cs?.vehicle_seats ? `${cs.vehicle_seats} 座${cs.vehicle_label || ""}` : "",
  ].filter(Boolean).join(" · ");

  return (
    <PageShell
      title={`${p?.name_cn ?? "产品售价"}（${p?.code ?? ""}）`}
      subtitle={subtitle}
      actions={
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">{STATUS_CN[p?.status ?? ""] ?? p?.status}</span>
          <Can perm="products.products.update"><Link to={`/products/${id}/edit?step=4`} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-[13px] font-medium text-slate-600 hover:bg-slate-50" title="改成本输入：必付项目、团人数、排期（车型/房型在第③步资源绑定）"><Pencil className="h-3.5 w-3.5" /> 编辑成本/必付</Link></Can>
          <Can perm="pricing.channel_config.update"><Link to={`/products/${id}?tab=options`} className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white hover:bg-blue-700" title="改价统一在产品的「选项 › 售价」里（第一个选项 = 产品价）"><Tag className="h-3.5 w-3.5" /> 改价</Link></Can>
          <Link to="/admin/pricing" className="flex h-9 items-center gap-1 rounded-lg border border-slate-200 px-3 text-[13px] font-medium text-slate-600 hover:bg-slate-50"><ChevronLeft className="h-3.5 w-3.5" /> 返回总览</Link>
        </div>
      }
    >
      <PricingSubnav />
      <div className="text-[12px] text-slate-400">售价管理中心 › 售价总览 › {p?.name_cn}</div>
      <OptionSwitch options={optionList} value={optId ?? optionList.find((o) => o.is_default)?.id ?? null} onChange={(oid) => setOptPick(optionList.find((o) => o.id === oid)?.is_default ? null : oid)}
        note="成本明细、价表、保本表按这里选的选项算；团期、库存、车辆今日版各选项共用。" />

      {/* 三桶 */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card title="① 套餐价（车+酒店+司机）">
          <div className="text-[22px] font-bold text-slate-900">成本 {usd(cs?.package_cost_per_person_cents)} / 人</div>
          <div className="mt-1 text-[12px] text-slate-500">同行 {usd(b2b?.list_price_cents)} · 散客 {usd(direct?.list_price_cents)}{canCost && <> · 加成 {b2b?.markup_pct ?? "—"}% / {direct?.markup_pct ?? "—"}% · 毛利率 {b2b?.margin_pct ?? "—"}% / {direct?.margin_pct ?? "—"}%</>}</div>
        </Card>
        <Card title="② 门票（客人自付）">
          {/* TIXSEL-1 · 过路账只统计自费 / 必付景点的票；「已含」的票已经在 ① 套餐价里，这里不再算一遍 */}
          {c && (c.ticket_total_cents ?? 0) === 0 && ((c.included_tickets?.length ?? 0) > 0 || c.ticket_cost_missing) ? (
            <>
              <div className="text-[18px] font-bold leading-[33px] text-slate-900">无（门票已含在套餐里）</div>
              <div className="mt-1 text-[12px] text-slate-500">明细见下方「门票（已含）」· 没有客人另付的门票</div>
            </>
          ) : (
            <>
              <div className="text-[22px] font-bold text-slate-900">{usd(c?.ticket_total_cents)} / 人</div>
              <div className="mt-1 text-[12px] text-slate-500">自费 / 必付景点 · 过路账 · 0 毛利 · 客人单独付 · 每景点一张成人票（儿童价下单另算）</div>
            </>
          )}
        </Card>
        <Card title="③ 必付项目" className="border-emerald-200">
          <div className="text-[22px] font-bold text-emerald-600">{usd(cs?.mandatory_fee_cents)} / 人</div>
          <div className="mt-1 text-[12px] text-slate-500">利润中心 · 近纯利 · 客人单独付 · 手动可改</div>
        </Card>
      </div>

      {/* 套餐成本拆解 */}
      <Card title={`套餐成本拆解（${cs?.vehicle_seats ? cs.vehicle_seats + " 座" : "车队"} · 满团 ${maxPax} 人 · 双人房）`}>
        {calcQ.isError ? <div className="py-6 text-center text-[13px] text-slate-500">成本拆解加载失败 · <button onClick={() => calcQ.refetch()} className="text-blue-600 hover:underline">重试</button></div> : calcQ.isLoading ? <LoadingState /> : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[13px]">
              <tbody>
                {/* PMODE-1 · manual 定价方式不按车队/房价算——两行显「—（手填）」，
                    套餐成本/人直接是手填值（下面那一行照常显 c?.package_cost_per_person_cents，
                    引擎短路后这个值本来就等于 manual_cost_per_person_cents，不用另外改）。 */}
                <tr className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-600">
                    {p?.pricing_mode === "manual" ? "车队日成本（手填定价方式不适用）" : `车队日成本（含油+司机）　${usd(c?.fleet_daily_cost_cents)}/天 × 大车 ${c?.bus_days ?? c?.total_days ?? "—"} 天${c?.bus_days != null && c.bus_days !== c.total_days ? `（行程 ${c.total_days} 天，接送机日不坐大车）` : ""} ÷ ${maxPax} 人`}
                    {/* COSTADJ-1 · 本产品单独调整：标记 + 车辆资料原值删除线 */}
                    {p?.pricing_mode !== "manual" && c?.fleet_daily_source === "product_override" && (
                      <span className="ml-2 inline-flex items-center gap-1.5" data-testid="fleet-override-tag">
                        <span className="rounded-full bg-amber-50 px-1.5 py-px text-[10.5px] font-semibold text-amber-600">本产品单独调整</span>
                        {!!c.fleet_daily_vehicle_cents && <span className="text-[11.5px] text-slate-400">车辆资料 <s>{usd(c.fleet_daily_vehicle_cents)}</s>/天</span>}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{p?.pricing_mode === "manual" ? "—（手填）" : `${usd(c?.fleet_per_person_cents)} / 人`}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-600">
                    {p?.pricing_mode === "manual" ? "酒店（手填定价方式不适用）" : `酒店　${usd(roomNightCents)}/晚 × ${c?.total_nights ?? "—"} 晚 ÷ 2 人/间`}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{p?.pricing_mode === "manual" ? "—（手填）" : `${usd(hotelBase2)} / 人`}</td>
                </tr>
                {/* TRANSCOST-1 · 接送机成本：每趟全包（含油 + 司机），一车一趟一个价；成本表按每趟 2 人估，订单整趟进本单 */}
                {p?.pricing_mode !== "manual" && c && (c.transfer_pickup_source !== "none" || c.transfer_dropoff_source !== "none") && (
                  <tr className="border-b border-slate-100" data-testid="transfer-cost-row">
                    <td className="px-4 py-3 text-slate-600">
                      接送机　{[
                        c.transfer_pickup_source !== "none" ? `接机 ${c.transfer_pickup_cents != null ? usd(c.transfer_pickup_cents) : "未填"}` : null,
                        c.transfer_dropoff_source !== "none" ? `送机 ${c.transfer_dropoff_cents != null ? usd(c.transfer_dropoff_cents) : "未填"}` : null,
                      ].filter(Boolean).join(" + ")} / 趟 ÷ {c.transfer_pax ?? 2} 人（按每趟 {c.transfer_pax ?? 2} 人估）
                      {c.transfer_cost_missing && (
                        <span className="ml-2 rounded bg-rose-50 px-1.5 py-px text-[11.5px] font-semibold text-rose-600" data-testid="transfer-cost-missing">
                          {[c.transfer_pickup_source === "unset" ? "接机成本未填" : null, c.transfer_dropoff_source === "unset" ? "送机成本未填" : null].filter(Boolean).join("、")}（未算入套餐成本，不当 $0）
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{usd(c.transfer_per_person_cents)} / 人</td>
                  </tr>
                )}
                {/* COSTADJ-1 · 午餐：行程勾了午餐的天 × 本产品餐标；勾了没填 = 红字，不当 $0 */}
                {p?.pricing_mode !== "manual" && c && (c.lunch_days ?? 0) > 0 && (
                  <tr className="border-b border-slate-100" data-testid="lunch-cost-row">
                    <td className="px-4 py-3 text-slate-600">
                      午餐　行程 {c.lunch_days} 天含午餐 · {c.lunch_days} 餐 × 本产品餐标{!c.lunch_cost_missing && c.lunch_days ? ` ${usd((c.lunch_per_person_cents ?? 0) / c.lunch_days)}` : ""}
                      {c.lunch_cost_missing && (
                        <span className="ml-2 rounded bg-rose-50 px-1.5 py-px text-[11.5px] font-semibold text-rose-600" data-testid="lunch-cost-missing">午餐餐标未填，这一项没算进成本（不当 $0）</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{c.lunch_cost_missing ? "未填" : `${usd(c.lunch_per_person_cents)} / 人`}</td>
                  </tr>
                )}
                {/* INTERCITY-1 · 城际用车：财务价目 × 方向，按人，满车不满车都一样；每段一行说明，右侧是合计 */}
                {p?.pricing_mode !== "manual" && c && (c.intercity_legs?.length ?? 0) > 0 && (
                  <tr className="border-b border-slate-100 bg-blue-50/40" data-testid="intercity-cost-row">
                    <td className="px-4 py-3 text-slate-600">
                      城际用车
                      {(c.intercity_legs ?? []).map((l) => (
                        <div key={l.route_id} className="text-[11.5px] text-slate-400" data-testid="intercity-cost-detail">
                          {l.route_name_cn} · {INTERCITY_DIRECTION_LABEL[l.direction]} · {l.formula}（${(l.amount_cents / 100).toFixed(2)}）
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">${((c.intercity_per_person_cents ?? 0) / 100).toFixed(2)} / 人</td>
                  </tr>
                )}
                {/* TIXSEL-1 · 门票（已含）：已含且选了票种的景点，每人 1 张，单位成本 = 订单门票行成本口径；已含有票种没选 → 红字，这一项没算入（不当 $0） */}
                {p?.pricing_mode !== "manual" && c && ((c.included_tickets?.length ?? 0) > 0 || c.ticket_cost_missing) && (
                  <tr className="border-b border-slate-100" data-testid="included-ticket-row">
                    <td className="px-4 py-3 text-slate-600">
                      门票（已含）
                      {(c.included_tickets ?? []).length > 0 && (
                        <div className="text-[11.5px] text-slate-400" data-testid="included-ticket-detail">
                          {(c.included_tickets ?? []).map((t) => `${t.attraction_name} · ${t.ticket_name} $${(t.unit_cents / 100).toFixed(2)}`).join("；")}
                          （自费 / 必付景点不算）
                        </div>
                      )}
                      {c.ticket_cost_missing && (
                        <span className="ml-2 rounded bg-rose-50 px-1.5 py-px text-[11.5px] font-semibold text-rose-600" data-testid="included-ticket-missing">
                          {(c.ticket_missing_names ?? []).join("、")}：已含但没选票种，这一项没算进成本（不当 $0）
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{c.ticket_cost_missing && !(c.included_tickets ?? []).length ? "未选" : `$${((c.ticket_included_per_person_cents ?? 0) / 100).toFixed(2)} / 人`}</td>
                  </tr>
                )}
                <tr className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-600">门票　过路账（客人单独付）</td>
                  <td className="px-4 py-3 text-right text-slate-400">不进成本</td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-800">套餐成本 / 人</td>
                  <td className="px-4 py-3 text-right text-[16px] font-bold text-slate-900">{usd(c?.package_cost_per_person_cents)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[13px] font-medium text-slate-700">同行建议零售价（{b2bRetailTag(occBQ.data?.[0]?.commission_pct)}）{usd(b2b?.list_price_cents)} · 同行结算价 {usd(b2b?.company_net_cents)}</div>
            {canCost && <div className="mt-1 text-[12px] font-semibold text-blue-600">加成 {b2b?.markup_pct ?? "—"}% · 毛利率 {b2b?.margin_pct ?? "—"}%（结算价口径）{b2b?.gross_margin_pct != null ? ` · 挂牌价口径 ${b2b.gross_margin_pct}%` : ""}</div>}
            <div className="mt-1 text-[11px] text-slate-400">同行站显示的是结算价，同行下单也按结算价扣额度</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[13px] font-medium text-slate-700">散客价 {usd(direct?.list_price_cents)} · 公司净 {usd(direct?.company_net_cents)}（无佣金）</div>
            {canCost && <div className="mt-1 text-[12px] font-semibold text-emerald-600">加成 {direct?.markup_pct ?? "—"}% · 毛利率 {direct?.margin_pct ?? "—"}%</div>}
          </div>
        </div>
      </Card>

      {/* 按住房人数档售价 */}
      <Card title="按住房人数档售价（同行价目表 · 全年统一价）" action={<span className="text-[11px] text-slate-400">门票 / 必付另付</span>}>
        <OvernightMissingBar missing={occBQ.data?.[0]?.overnight_missing} />
        {occBQ.isError ? <div className="py-6 text-center text-[13px] text-slate-500">售价档加载失败 · <button onClick={() => occBQ.refetch()} className="text-blue-600 hover:underline">重试</button></div> : occBQ.isLoading ? <LoadingState /> : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3 text-left">住房档</th><th className="px-4 py-3 text-left">成本/人</th><th className="px-4 py-3 text-left">同行建议零售价/人（{b2bRetailTag(occBQ.data?.[0]?.commission_pct)}）</th><th className="px-4 py-3 text-left">同行结算价/人</th><th className="px-4 py-3 text-left">散客价/人</th><th className="px-4 py-3 text-left">API 平台价/人</th><th className="px-4 py-3 text-left">毛利率 同/散</th></tr>
              </thead>
              <tbody>
                {occBQ.data?.map((r) => {
                  const d = occDQ.data?.find((x) => x.occupancy === r.occupancy);
                  const a = occAQ.data?.find((x) => x.occupancy === r.occupancy);
                  return (
                    <tr key={r.occupancy} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-medium text-slate-800">{r.occupancy_label}</td>
                      <td className="px-4 py-3 text-slate-600">{usd(r.package_cost_per_person_cents)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{usd(r.list_price_cents)}</td>
                      <td className="px-4 py-3 font-semibold text-blue-700">{usd(r.company_net_cents)}</td>
                      <td className="px-4 py-3 text-slate-600">{usd(d?.list_price_cents)}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="font-semibold text-slate-900">{usd(a?.list_price_cents)}</div>
                        {/* MFEE-1 · GYG/Bokun/OCTO 不许标价之外再收强制费，必付费已并入这个价 */}
                        {!!a?.included_mandatory_fee_cents && (
                          <div className="text-[11px] text-amber-600">含必付 {usd(a.included_mandatory_fee_cents)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.margin_pct == null ? "—" : `${r.margin_pct}%`} / {d?.margin_pct == null ? "—" : `${d.margin_pct}%`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 保本测算 + 必付/节假日系数 */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {canCost && <Card title="按团人数保本测算（内部 · 成本可见限定）">
          {/* BKEVEN-1 · 车型 Tab——任意一个团都可能两种车型混用，按实际收团情况再定车，
              用 Tab 切换分别看两张表；只有一个车型时不显示 Tab 条（和现在一样） */}
          {vehicleOptions.length > 1 && (
            <div className="mb-3 flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
              {vehicleOptions.map((v, i) => (
                <button
                  key={v.model_label_cn}
                  type="button"
                  onClick={() => setActiveTabIdx(i)}
                  className={`rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition-colors ${
                    activeTabIdx === i ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-white"
                  }`}
                >
                  {v.model_label_cn} {v.seat_capacity} 座 · {v.passenger_capacity} 客
                </button>
              ))}
            </div>
          )}
          {beQ.isLoading ? <LoadingState /> : (
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-[13px]">
                <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <tr><th className="px-4 py-3 text-left">团人数</th><th className="px-4 py-3 text-left">车摊/人</th><th className="px-4 py-3 text-left">成本/人</th><th className="px-4 py-3 text-left">毛利率</th><th className="px-4 py-3 text-left">状态</th></tr>
                </thead>
                <tbody>
                  {beQ.data?.map((r) => {
                    const dot = r.status === "green" ? "bg-emerald-500" : r.status === "yellow" ? "bg-amber-500" : r.status === "red" ? "bg-rose-500" : "bg-slate-300";
                    const profit = r.profit_per_person_cents;
                    const profitStr = profit == null ? "—" : profit < 0 ? `亏 ${usd(-profit)}` : `赚 ${usd(profit)}${r.margin_pct != null ? ` · ${r.margin_pct}%` : ""}`;
                    return (
                      <tr key={r.pax} className="border-t border-slate-100">
                        <td className="px-4 py-3 font-medium text-slate-800">{r.pax} 人{r.pax === maxPax ? "（满）" : ""}</td>
                        <td className="px-4 py-3 text-slate-600">{usd(carPerPerson(r.pax))}</td>
                        <td className="px-4 py-3 text-slate-600">{usd(r.package_cost_per_person_cents)}</td>
                        <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${dot}`} />{profitStr}</span></td>
                        <td className="px-4 py-3 text-slate-500">{r.status === "green" ? "健康" : r.status === "yellow" ? "薄利" : r.status === "red" ? "亏损" : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-2 text-[11px] text-slate-400">💡 固定价下·车队成本÷N → 小团成本高（红/亏）·大团补小团（口径④A）。</div>
        </Card>}

        <Card title="必付项目 · 节假日系数">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-medium text-slate-600">必付项目 / 人（手动）</span>
              <span className="rounded bg-white px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">可改</span>
            </div>
            <div className="mt-1 text-[20px] font-bold text-emerald-600">{usd(cs?.mandatory_fee_cents)}</div>
            <div className="mt-1 text-[11px] text-slate-500">客人单独付 · 近纯利 · 随产品/市场随时调</div>
          </div>
          <div className="mt-3">
            <div className="mb-2 text-[12px] font-medium text-slate-600">节假日系数</div>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-[13px]">
                <tbody>
                  <tr className="border-b border-slate-100"><td className="px-4 py-2 text-slate-600">平季</td><td className="px-4 py-2 text-right font-semibold text-slate-900">× 1.00</td></tr>
                  {holQ.data?.map((h) => (
                    <tr key={h.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-2 text-slate-600">{h.name}</td>
                      <td className="px-4 py-2 text-right font-semibold text-slate-900">× {(1 + h.uplift_pct / 100).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-[11px] text-slate-400">酒店实际涨价另自动传导到成本（双轨：成本自动 + 手动旺季系数）。</div>
          </div>
        </Card>
      </div>
    </PageShell>
  );
}
